import { AbstractControl, ControlValueAccessor, NgControl, TouchedChangeEvent } from '@angular/forms';
import { ChangeDetectorRef, Directive, inject, Injector, OnInit, runInInjectionContext, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Fn } from '@coin/shared/core/util-models';
import { errorsToText } from './error-to-text.helper';
import { filter, map, merge, of, startWith, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

@Directive() // decorator only to enable lifecycle compatibility
export abstract class DefaultControlValueAccessor<T> implements ControlValueAccessor, OnInit {
  protected cd = inject(ChangeDetectorRef);
  protected injector = inject(Injector);
  protected translateService = inject(TranslateService);

  protected ngControl = signal<NgControl | null>(null);
  private ngControl$ = toObservable(this.ngControl);

  // automatic tooltip dependent on form errors
  public errorTooltip = toSignal(
    this.ngControl$.pipe(
      filter(Boolean),
      switchMap(ngControl =>
        merge(
          runInInjectionContext(this.injector, () => toObservable(this.disabled)),
          ngControl.statusChanges
        ).pipe(
          startWith(null),
          map(() => {
            if (!ngControl.errors) return null;
            return errorsToText(ngControl.errors)
              .map(error => this.translateService.instant(error))
              .join('\n\n');
          })
        )
      )
    )
  );

  public touched = toSignal(
    this.ngControl$.pipe(
      switchMap(ngControl => {
        if (!ngControl?.control) return of(false);
        return ngControl.control.events.pipe(
          filter((event): event is TouchedChangeEvent => event instanceof TouchedChangeEvent),
          map(event => event.touched),
          startWith(ngControl.control.touched)
        );
      })
    )
  );

  private _disabled = signal(false);
  public disabled = this._disabled.asReadonly();

  public ngOnInit(): void {
    this.ngControl.set(this.injector.get(NgControl, null));
  }

  protected get isRequired(): boolean {
    const validator = this.ngControl()?.control?.validator;
    if (!validator) return false;

    return !!validator({} as AbstractControl)?.required;
  }

  // for manual (via ui) value changes
  protected _value: T;
  get value(): T {
    return this._value;
  }
  set value(value: T) {
    this._value = value;
    this.onChange(value);
  }

  // for programmatic value changes
  writeValue(value: T) {
    this._value = value;
    this.cd.markForCheck();
  }

  onChange: Fn = () => {};
  registerOnChange(fn: Fn) {
    this.onChange = fn;
  }

  onTouch: Fn = () => {};
  registerOnTouched(fn: Fn) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this._disabled.set(isDisabled);
  }
}
